/**
 * Organization Management Redux Slice
 * 
 * Handles multi-organization state management for enterprise users
 * Features:
 * - Organization fetching and caching
 * - Automatic organization selection with localStorage persistence
 * - New user onboarding flow
 * - Organization context switching
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";
import { localStoreConfig } from "src/app/constants";

const initialState = {
  organizations: [],
  selectedOrganization: null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  error: null,
  
  // Organization switching
  needsDataRefresh: false,
  
  // Onboarding state
  isOnboardingLoading: false,
  isOnboardingError: false,
  isOnboardingSuccess: false,
  onboardingError: null,
};

/**
 * Helper function to auto-select organization based on localStorage or fallback to first
 */
const autoSelectOrganization = (organizations) => {
  if (!organizations || organizations.length === 0) return null;
  
  const storedOrgId = localStorage.getItem(localStoreConfig.selectedOrganizationId);
  let selectedOrg = null;
  
  if (storedOrgId) {
    selectedOrg = organizations.find(org => org.organizationId === storedOrgId);
  }
  
  if (!selectedOrg) {
    selectedOrg = organizations[0];
    localStorage.setItem(localStoreConfig.selectedOrganizationId, selectedOrg.organizationId);
  }
  
  return selectedOrg;
};

/**
 * Fetch user's organizations from API
 */
export const fetchUserOrganizations = createAsyncThunk(
  "organization/fetchUserOrganizations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosApi().get("/user/public/organizations");
      return response.data.data || [];
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch organizations";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Complete organization onboarding for new users
 */
export const completeOrganizationOnboarding = createAsyncThunk(
  "organization/completeOnboarding",
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token }).post("/user/public/complete-onboarding", formData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to complete onboarding";
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Complete organization onboarding for new users
 */
export const createOrganization = createAsyncThunk(
  "organization/createOrganization",
  async ({ token, organizationId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).post("/organization", formData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to create organization";
      return rejectWithValue(errorMessage);
    }
  }
);

export const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    /**
     * Reset loading and error states
     */
    resetState: (state) => {
      state.isSuccess = false;
      state.isError = false;
      state.isLoading = false;
      state.error = null;
      state.isOnboardingLoading = false;
      state.isOnboardingError = false;
      state.isOnboardingSuccess = false;
      state.onboardingError = null;
    },
    
    /**
     * Manually select an organization
     */
    selectOrganization: (state, action) => {
      const { organizationId } = action.payload;
      const organization = state.organizations.find(org => org.organizationId === organizationId);
      
      if (organization) {
        state.selectedOrganization = organization;
        localStorage.setItem(localStoreConfig.selectedOrganizationId, organizationId);
      }
    },
    
    /**
     * Switch to a new organization and trigger data refresh
     * This action should be used instead of selectOrganization when you want to clear cached data
     */
    switchOrganization: (state, action) => {
      const { organizationId } = action.payload;
      const organization = state.organizations.find(org => org.organizationId === organizationId);
      
      if (organization && state.selectedOrganization?.organizationId !== organizationId) {
        state.selectedOrganization = organization;
        localStorage.setItem(localStoreConfig.selectedOrganizationId, organizationId);
        // Mark that organization data needs refresh
        state.needsDataRefresh = true;
      }
    },
    
    /**
     * Mark data refresh as completed
     */
    markDataRefreshComplete: (state) => {
      state.needsDataRefresh = false;
    },
    
    /**
     * Clear all organization data (logout)
     */
    clearOrganizations: (state) => {
      state.organizations = [];
      state.selectedOrganization = null;
      localStorage.removeItem(localStoreConfig.selectedOrganizationId);
    },
  },
  
  extraReducers: (builder) => {
    // Fetch organizations
    builder
      .addCase(fetchUserOrganizations.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.error = null;
      })
      .addCase(fetchUserOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.organizations = action.payload;
        state.selectedOrganization = autoSelectOrganization(action.payload);
      })
      .addCase(fetchUserOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      });
    
    // Complete onboarding
    builder
      .addCase(completeOrganizationOnboarding.pending, (state) => {
        state.isOnboardingLoading = true;
        state.isOnboardingError = false;
        state.onboardingError = null;
      })
      .addCase(completeOrganizationOnboarding.fulfilled, (state, action) => {
        state.isOnboardingLoading = false;
        state.isOnboardingSuccess = true;
        
        // Update the organization data in the list
        const updatedOrgData = action.payload.data;
        const orgIndex = state.organizations.findIndex(org => org.organizationId === updatedOrgData.organizationId);
        
        if (orgIndex !== -1) {
          // Update organization in the list
          state.organizations[orgIndex] = { 
            ...state.organizations[orgIndex], 
            organizationName: updatedOrgData.organizationName,
            companyType: updatedOrgData.companyType 
          };
          
          // Update selected organization if it's the same one
          if (state.selectedOrganization?.organizationId === updatedOrgData.organizationId) {
            state.selectedOrganization = { 
              ...state.selectedOrganization, 
              organizationName: updatedOrgData.organizationName,
              companyType: updatedOrgData.companyType 
            };
          }
        }
      })
      .addCase(completeOrganizationOnboarding.rejected, (state, action) => {
        state.isOnboardingLoading = false;
        state.isOnboardingError = true;
        state.onboardingError = action.payload;
      });
    
    // Create organization
    builder
      .addCase(createOrganization.pending, (state) => {
        state.isOnboardingLoading = true;
        state.isOnboardingError = false;
        state.onboardingError = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isOnboardingLoading = false;
        state.isOnboardingSuccess = true;
        
        // The organization will be fetched fresh, so just mark success
        console.log('Company created successfully:', action.payload);
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isOnboardingLoading = false;
        state.isOnboardingError = true;
        state.onboardingError = action.payload;
      });
  },
});

export const { 
  resetState, 
  selectOrganization, 
  switchOrganization,
  markDataRefreshComplete,
  clearOrganizations 
} = organizationSlice.actions;

export default organizationSlice.reducer;